import { AxiosError } from 'axios';
import { useState, useEffect } from 'react';
import { apimCall, HttpHeaders, HttpMethod } from '../helpers/api';

const cache = {};

function useApimWithOptions<T>(
  serviceName,
  url: string,
  skip?: boolean,
  callback?: (T) => void,
  preventCaching?: boolean,
  method: HttpMethod = 'GET',
  body: object = null,
  headers: HttpHeaders = null
): [result: T, loading: boolean, loaded: boolean, refresh: () => void, setResult: (data: T) => void, error: AxiosError, refreshing: boolean] {
  const [result, setResult] = useState<T>();
  const [error, setError] = useState<AxiosError>(null);
  // if loading is not set, then the default value is undefined
  // which is the same as not loading when initially used
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [invokeRefresh, setInvokeRefresh] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  const setFetchingState = () => {
    setLoading(true);
    setError(null);
  };

  const setFetchedState = (response) => {
    setResult(response);
    setLoading(false);
    setRefreshing(false);
    setLoaded(response != null);
  };

  const refresh = () => {
    setFetchingState();
    setRefreshing(true);
    setInvokeRefresh({});
  };

  useEffect(() => {
    setFetchingState();
    const cacheKeySuffix = body && !preventCaching ? `|body=${JSON.stringify(body)}` : '';
    const cacheKey = `method=${method}|url=${url}${cacheKeySuffix}`;

    if (skip) {
      setFetchedState(null); //skipped state does not have a value

      if (callback) {
        callback(null);
      }

      return;
    }

    if (cache[cacheKey] && !preventCaching && !refreshing) {
      setFetchedState(cache[cacheKey]);
      //do not retun. we still want to fetch the results so we can refresh with new results
    }

    apimCall(serviceName, url, method, body, null, headers)
      .then((r) => {
        if (!preventCaching) {
          cache[cacheKey] = r.data;
        }
        setFetchedState(r.data);

        if (callback) {
          callback(r.data);
        }
      })
      .catch((e) => {
        console.error(e);
        setError(e);
        setFetchedState(null);
        if (callback) {
          callback(null);
        }
      });
  }, [url, invokeRefresh, skip, preventCaching, method, body ? JSON.stringify(body) : null, headers ? JSON.stringify(headers) : null]);

  return [result, loading, loaded, refresh, setResult, error, refreshing];
}

export function useApim<T>(serviceName: string, url: string, skip?: boolean, preventCaching?: boolean) {
  return useApimWithOptions<T>(serviceName, url, skip, null, preventCaching, 'GET', null);
}

export function useApimPost<T>(serviceName: string, url: string, body?: any, skip?: boolean, preventCaching?: boolean, headers?: HttpHeaders) {
  return useApimWithOptions<T>(serviceName, url, skip, null, preventCaching, 'POST', body, headers);
}

export function useApimWithCallback<T>(serviceName: string, url: string, callback?: (T) => void, preventCaching?: boolean) {
  return useApimWithOptions<T>(serviceName, url, false, callback, preventCaching, 'GET', null);
}
